import { getConformanceOptions } from "@/constants/statement-constants";
import type { Step1Form, Step2Form } from "@/constants/statement-constants";

export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Substitutes the 6 placeholders in an HTML statement template with real values.
 * The `conformanceStatus` value is first looked up in the English `CONFORMANCE_OPTIONS`
 * labels (for admin preview which is always English); if not found the raw stored value is used.
 */
export function fillStatementTemplate(
  template: string,
  step1: Step1Form,
  step2: Step2Form,
  generatedDate?: string,
): string {
  const e = escapeHtml;

  const companyName = e(step1.companyName || "This organisation");
  const websiteUrl = e(step1.website || "this website");
  const contactEmail = e(step1.businessEmail);
  const standard = e(step2.wcagStandard);

  const conformanceLabel =
    getConformanceOptions().find((o) => o.value === step2.conformanceStatus)?.label ??
    step2.conformanceStatus;
  const conformance = e(conformanceLabel);

  const lastUpdated = generatedDate
    ? new Date(generatedDate).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

  return template
    .replace(/\{\{company_name\}\}/g, companyName)
    .replace(/\{\{website_url\}\}/g, websiteUrl)
    .replace(/\{\{accessibility_standard\}\}/g, standard)
    .replace(/\{\{conformance_status\}\}/g, conformance)
    .replace(/\{\{contact_email\}\}/g, contactEmail)
    .replace(/\{\{last_updated\}\}/g, lastUpdated);
}

export function formatDate(iso: string): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.innerText || div.textContent || "";
}

/**
 * Copies text to clipboard.
 * Uses the modern Clipboard API when available (HTTPS / localhost),
 * and falls back to document.execCommand for plain HTTP environments.
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for HTTP (non-secure) contexts
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    const success = document.execCommand("copy");
    if (!success) throw new Error("execCommand copy failed");
  } finally {
    document.body.removeChild(textarea);
  }
}
